import type {Evaluator} from "@cm/graph-v3"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {MaterialNode} from "#template/nodes/material/material-node"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import {imageLike} from "#template/nodes/image/fundamental"
import type {ImageLikeNode} from "#template/nodes/types"
import {visitNone} from "#template/template-node/visit-helpers"
import {
    BsdfDiffuse,
    Mapping,
    MaterialConnection,
    materialConnection,
    MaterialGraph,
    MaterialNode as MaterialGraphNode,
    OutputMaterial,
    TexCoord,
} from "@cm/material-nodes"

export type BackgroundMaterialParameters = {
    backgroundImage: ImageLikeNode | null
}

const backgroundMaterialDefinition: TemplateNodeDefinition<BackgroundMaterialParameters> = {
    nodeClass: "BackgroundMaterial",
    validation: {
        parameters: z.object({backgroundImage: z.nullable(imageLike)}),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class BackgroundMaterial extends MaterialNode<BackgroundMaterialParameters> {
    public override get definition() {
        return backgroundMaterialDefinition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<BackgroundMaterialParameters> {
        if (this.parameters.backgroundImage === null) return skipped
        return visitNone(this.parameters)
    }

    override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<MaterialGraph | null> {
        const backgroundImage = this.parameters.backgroundImage ? evaluator.evaluate(this.parameters.backgroundImage) : null

        return scope.pureLambda(
            backgroundImage,
            (backgroundImage) => {
                if (!backgroundImage) return null

                const texCoord = new TexCoord({parameters: {inputs: {}, settings: {}}})

                const mapping = new Mapping({
                    parameters: {
                        inputs: {
                            vector: materialConnection(texCoord, "window"),
                        },
                        settings: {
                            vectorType: "POINT",
                            location: {x: 0, y: 0, z: 0},
                            rotation: {x: 0, y: 0, z: 0},
                            scale: {x: 1, y: 1, z: 1},
                        },
                    },
                })

                const {color} = backgroundImage.imageNode.generator({
                    uv: materialConnection(mapping, "vector"),
                    extension: "REPEAT",
                    interpolation: "Linear",
                    projection: "FLAT",
                })

                const shader = new BsdfDiffuse({
                    parameters: {
                        inputs: {color},
                        settings: {},
                    },
                })

                const output = new OutputMaterial({
                    parameters: {
                        inputs: {surface: materialConnection(shader, "bsdf")},
                        settings: {},
                    },
                })

                return new MaterialGraph({
                    parameters: {
                        connections: [...output].filter((nodeOrConnection) => nodeOrConnection instanceof MaterialConnection),
                        nodes: [...output].filter((nodeOrConnection) => nodeOrConnection instanceof MaterialGraphNode),
                        materialRevisionLegacyId: 0,
                        materialLegacyId: 0,
                        name: `BackgroundMaterial-${backgroundImage.imageNode.hash}`,
                    },
                })
            },
            "backgroundImage",
        )
    }
}
